/**
 * Helper functions for Compensation Leave
 */
import moment from 'moment';

export const TABS = {
    TARGET: 'target',
    OVERTIME_DETAIL: 'overtimeDetail',
};

export const ALL_OPTION = 'ALL';

export const ZERO_TIME = '00:00';

/** Rows with createdAt are already generated — not selectable. */
export const isOvertimeDetailSelectable = (row) => {
    const createdAt = row?.createdAt;
    return createdAt == null || createdAt === '';
};

/** Manual generation status label: YES / NO / '-'. */
export const formatManualStatus = (row, t) => {
    if (row?.manual === true) return t('compensationLeave.yes');
    if (row?.manual === false) return t('compensationLeave.no');
    return '-';
};

/** Generation date&time: shown only when manual is true/false and date is valid. */
export const formatGenerationDateTime = (row, value = row?.createdAt) => {
    const isManualSet = row?.manual === true || row?.manual === false;
    if (!isManualSet || !value || !moment(value).isValid()) return '-';
    return moment(value).format('YYYY-MM-DD HH:mm');
};

/** Format API duration: HH:mm string or seconds (number). */
export const formatDurationValue = (value) => {
    if (value == null || value === '') {
        return ZERO_TIME;
    }

    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        const absSeconds = Math.abs(value);
        const totalMinutes = Math.floor(absSeconds / 60);
        const remainingSeconds = absSeconds % 60;

        const roundedMinutes = totalMinutes + (remainingSeconds >= 30 ? 1 : 0);

        const hours = Math.floor(roundedMinutes / 60);
        const minutes = roundedMinutes % 60;
        const sign = value < 0 ? '-' : '';

        return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    return ZERO_TIME;
};

export const HEADER_ROW_COUNT = 2;

export const HEADER_GROUP_LABEL_KEYS = {
    overtime: 'compensationLeave.overtimeHours',
    leave: 'compensationLeave.leaveHours',
};

export const getTabsConfig = (t) => [
    {
        key: TABS.TARGET,
        label: t('compensationLeave.tabTarget'),
        name: 'compensationLeaveTarget',
    },
    {
        key: TABS.OVERTIME_DETAIL,
        label: t('compensationLeave.tabOvertimeDetail'),
        name: 'compensationLeaveOvertimeDetail',
    },
];

export const getSearchFieldsConfig = ({ t, hireTypeList, workPlaceList }) => {
    const commonFields = [
        {
            name: 'name',
            type: 'input',
            placeholder: t('compensationLeave.searchPlaceholder'),
            fieldName: 'searchUserInput',
        },
        {
            name: 'hireType',
            type: 'select',
            placeholder: t('compensationLeave.selectHireType'),
            options: () => [{ value: ALL_OPTION, label: t('common.label.all') }, ...hireTypeList],
            fieldName: 'hireTypeSelect',
        },
        {
            name: 'area',
            type: 'select',
            placeholder: t('compensationLeave.workplace'),
            options: () => [{ value: ALL_OPTION, label: t('common.label.all') }, ...workPlaceList],
            fieldName: 'workPlaceSelect',
        },
    ];

    return { commonFields };
};

/** Shared overtime hour columns (tab 1 & 2 API field names) */
export const TARGET_OVERTIME_COLUMNS = [
    { key: 'overtime', nameKey: 'compensationLeave.extend' },
    { key: 'nightWork', nameKey: 'compensationLeave.night' },
    { key: 'nightOvertime', nameKey: 'compensationLeave.nightExtend' },
    { key: 'holidayWork', nameKey: 'compensationLeave.holidayWork' },
    { key: 'holidayOvertime', nameKey: 'compensationLeave.holidayExtend' },
    { key: 'holidayNightWork', nameKey: 'compensationLeave.holidayNight' },
    { key: 'holidayNightOvertime', nameKey: 'compensationLeave.holidayNightExtend' },
];

export const TARGET_LEAVE_COLUMNS = [
    { key: 'accruedExpected', nameKey: 'compensationLeave.occurExpected', variant: 'expected' },
    { key: 'accruedConfirmed', nameKey: 'compensationLeave.occurConfirmed', variant: 'confirmed' },
    { key: 'used', nameKey: 'compensationLeave.used' },
    { key: 'remaining', nameKey: 'compensationLeave.remaining' },
];

/** Tab 2 leave columns (overtime-details API) */
export const DETAIL_LEAVE_COLUMNS = [
    { key: 'accruedExpected', nameKey: 'compensationLeave.occurExpected', variant: 'expected' },
    { key: 'accruedConfirmed', nameKey: 'compensationLeave.occurConfirmed', variant: 'confirmed' },
];

const mapOvertimeColumns = (columns, t) =>
    columns.map((column) => ({
        grow: 1,
        width: column.key === 'holidayNightOvertime' ? 130 : 105,
        name: t(column.nameKey),
        key1: column.key,
        group: 'overtime',
    }));

/**
 * Column configurations for MultiGrid
 * Usage: getColumnsList(t)(TABS.TARGET | TABS.OVERTIME_DETAIL)
 */
export const getColumnsList = (t) => (tabName) => {
    const overtimeColumns = mapOvertimeColumns(TARGET_OVERTIME_COLUMNS, t);

    const byTab = {
        [TABS.TARGET]: [
            {
                grow: 1,
                width: 200,
                name: t('common.label.name'),
                key1: 'userInfo',
                rowSpan: HEADER_ROW_COUNT,
            },
            ...overtimeColumns,
            ...TARGET_LEAVE_COLUMNS.map((column) => ({
                grow: 1,
                width: 100,
                name: t(column.nameKey),
                key1: column.key,
                group: 'leave',
                variant: column.variant,
            })),
        ],
        [TABS.OVERTIME_DETAIL]: [
            {
                grow: 0,
                width: 50,
                name: '',
                key1: 'checkbox',
                rowSpan: HEADER_ROW_COUNT,
            },
            {
                grow: 1,
                width: 140,
                name: t('compensationLeave.companyNumber'),
                key1: 'companyNumber',
                rowSpan: HEADER_ROW_COUNT,
            },
            {
                grow: 1,
                width: 120,
                name: t('compensationLeave.fullName'),
                key1: 'userName',
                rowSpan: HEADER_ROW_COUNT,
            },
            {
                grow: 1,
                width: 180,
                name: t('compensationLeave.workDate'),
                key1: 'workDate',
                rowSpan: HEADER_ROW_COUNT,
            },
            {
                grow: 1,
                width: 90,
                name: t('compensationLeave.workStart'),
                key1: 'sworkTime',
                rowSpan: HEADER_ROW_COUNT,
            },
            {
                grow: 1,
                width: 90,
                name: t('compensationLeave.workEnd'),
                key1: 'eworkTime',
                rowSpan: HEADER_ROW_COUNT,
            },
            ...overtimeColumns,
            ...DETAIL_LEAVE_COLUMNS.map((column) => ({
                grow: 1,
                width: 110,
                name: t(column.nameKey),
                key1: column.key,
                group: 'leave',
                variant: column.variant,
            })),
            {
                grow: 1,
                width: 170,
                name: t('compensationLeave.generationDateTime'),
                key1: 'createdAt',
                rowSpan: HEADER_ROW_COUNT,
            },
            {
                grow: 1,
                width: 110,
                name: t('compensationLeave.manualGenerationStatus'),
                key1: 'manual',
                rowSpan: HEADER_ROW_COUNT,
            },
        ],
    };

    return byTab[tabName] || [];
};

export const getGroupRange = (columnData, group) => {
    const start = columnData.findIndex((column) => column.group === group);
    if (start < 0) {
        return null;
    }

    let end = start;
    while (end + 1 < columnData.length && columnData[end + 1].group === group) {
        end += 1;
    }

    return { start, end };
};

export const getGroupWidth = (columnData, group, getColumnWidth) => {
    const range = getGroupRange(columnData, group);
    if (!range) {
        return 0;
    }

    let total = 0;
    for (let index = range.start; index <= range.end; index += 1) {
        total += getColumnWidth
            ? getColumnWidth(index)
            : columnData[index]?.width || 0;
    }
    return total;
};

/**
 * Stretch columns to fill container width while keeping relative sizes.
 */
export const computeColumnWidths = (columnList, containerWidth) => {
    const widths = columnList.map((column) => column.width || 0);
    const total = widths.reduce((sum, width) => sum + width, 0);

    if (!containerWidth || total <= 0) {
        return widths;
    }

    if (total >= containerWidth) {
        return widths;
    }

    const extra = containerWidth - total;
    const growIndexes = columnList
        .map((column, index) => (column.grow === 0 ? -1 : index))
        .filter((index) => index >= 0);
    const targets = growIndexes.length > 0 ? growIndexes : columnList.map((_, index) => index);
    const baseExtra = Math.floor(extra / targets.length);
    let remainder = extra - baseExtra * targets.length;

    return widths.map((width, index) => {
        if (!targets.includes(index)) {
            return width;
        }

        let nextWidth = width + baseExtra;
        if (remainder > 0) {
            nextWidth += 1;
            remainder -= 1;
        }
        return nextWidth;
    });
};

export const getExcelColumnsList = (t) => [
    { name: t('compensationLeave.companyNumber'), key1: 'companyNumber' },
    { name: t('common.label.name'), key1: 'userName' },
    ...TARGET_OVERTIME_COLUMNS.map((column) => ({
        name: t(column.nameKey),
        key1: column.key,
    })),
    ...TARGET_LEAVE_COLUMNS.map((column) => ({
        name: t(column.nameKey),
        key1: column.key,
    })),
];

export const getOvertimeDetailExcelColumnsList = (t) => [
    { name: t('compensationLeave.companyNumber'), key1: 'companyNumber' },
    { name: t('compensationLeave.fullName'), key1: 'userName' },
    { name: t('compensationLeave.workDate'), key1: 'workDate' },
    { name: t('compensationLeave.workStart'), key1: 'sworkTime' },
    { name: t('compensationLeave.workEnd'), key1: 'eworkTime' },
    ...TARGET_OVERTIME_COLUMNS.map((column) => ({
        name: t(column.nameKey),
        key1: column.key,
    })),
    ...DETAIL_LEAVE_COLUMNS.map((column) => ({
        name: t(column.nameKey),
        key1: column.key,
    })),
    { name: t('compensationLeave.generationDateTime'), key1: 'createdAt' },
    { name: t('compensationLeave.manualGenerationStatus'), key1: 'manual' },
];

